import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import WhatsAppButton from '@/components/WhatsAppButton';
import {
  ChefHat,
  Calculator,
  TrendingUp,
  Bell,
  BarChart3,
  Clock,
  CheckCircle2,
  ArrowRight,
  Star,
  Mail,
  QrCode,
  ShoppingCart,
  FileSpreadsheet,
  ChevronDown,
} from 'lucide-react';
import { useState } from 'react';
import DemoRequestModal from '@/components/DemoRequestModal';

const features = [
  {
    icon: Calculator,
    title: 'Escandallos automáticos',
    description: 'Calcula el coste exacto de cada receta y ficha técnica con un clic. Incluye mermas, desperdicios y precios actualizados al instante.',
  },
  {
    icon: TrendingUp,
    title: 'Food Cost en tiempo real',
    description: 'Visualiza el food cost y la rentabilidad de cada plato con nuestro semáforo. Verde (<30%), amarillo (30-35%) o rojo (>35%).',
  },
  {
    icon: FileSpreadsheet,
    title: 'Adiós al Excel',
    description: 'Deja de hacer escandallos en hojas de cálculo. Actualiza el precio de un ingrediente y todos los costes se recalculan solos.',
  },
  {
    icon: Bell,
    title: 'Alertas de precios inteligentes',
    description: 'Recibe notificaciones cuando un ingrediente sube de precio o una receta supera el umbral de rentabilidad que tú definas.',
  },
  {
    icon: ShoppingCart,
    title: 'Gestión de compras y proveedores',
    description: 'Centraliza pedidos, facturas y albaranes. Escanea facturas con OCR y actualiza precios de ingredientes automáticamente.',
  },
  {
    icon: BarChart3,
    title: 'Reportes de rentabilidad',
    description: 'Dashboard con KPIs clave: coste promedio por ración, recetas más rentables, ingredientes más caros y evolución de márgenes.',
  },
  {
    icon: QrCode,
    title: 'Carta digital con QR',
    description: 'Diseña tu carta digital y genera un QR para que tus clientes la consulten desde el móvil. Sin apps, sin impresiones.',
  },
  {
    icon: ChefHat,
    title: 'Gestión de recetas y fichas técnicas',
    description: 'Organiza todas tus recetas con ingredientes, pasos, alérgenos y fotos. Duplica, adapta y escala para cualquier número de raciones.',
  },
];

const testimonials = [
  {
    name: 'María García',
    role: 'Chef ejecutiva, Restaurante El Olivo',
    content: 'Llevaba años poniendo las mermas a ojo. Un día me senté a calcular las del salmón y me salió un 35% — yo estaba poniendo un 10%. Todos mis escandallos estaban mal. PLEKO me sugirió las mermas correctas desde el primer día y por fin sé lo que me cuesta cada plato de verdad.',
    avatar: 'MG',
  },
  {
    name: 'Carlos Rodríguez',
    role: 'Propietario, Grupo Gastro BCN',
    content: 'El proveedor nos subió la merluza y no nos enteramos hasta que revisamos la caja a final de mes. Ya habíamos perdido dinero durante semanas. Ahora PLEKO nos avisa al momento y con el simulador vemos qué platos tocar antes de que sea tarde. Del 38% al 29% de food cost en 3 meses.',
    avatar: 'CR',
  },
  {
    name: 'Ana Martínez',
    role: 'Directora de operaciones, Catering Premium',
    content: 'Gestionamos 15 eventos al mes y cada lunes llegaban pilas de albaranes. Picábamos precios a mano, con errores, con retraso. Ahora hacemos una foto al albarán y en 10 segundos todos los precios están actualizados en las recetas. Es como tener una persona más en el equipo.',
    avatar: 'AM',
  },
  {
    name: 'Roberto López',
    role: 'Chef, Marisquería La Roca',
    content: 'En la marisquería compramos piezas enteras — atún, lubina, rape. Siempre poníamos el precio de factura en el escandallo, pero las espinas y las cabezas las tirábamos. Con el despiece de PLEKO descubrí que mi lomo de atún me costaba un 23% más de lo que creía. Ahora mis números cuadran.',
    avatar: 'RL',
  },
  {
    name: 'Laura Fernández',
    role: 'Propietaria, Bistró Estacional',
    content: 'Trabajo solo con producto de temporada y cada mes cambio la carta. Antes era un caos de precios, proveedores y escandallos nuevos. PLEKO me dice qué producto está en su mejor momento, calcula los costes al instante y me ahorro horas de trabajo cada semana. Por fin disfruto del cambio de carta.',
    avatar: 'LF',
  },
];

const faqItems = [
  {
    question: '¿Qué es un escandallo y por qué es importante en un restaurante?',
    answer: 'Un escandallo es el desglose detallado del coste de cada plato: ingredientes, cantidades, mermas y precio final por ración. Es la herramienta clave para saber si un plato es rentable o te está haciendo perder dinero. Sin escandallos actualizados, es imposible controlar el food cost ni fijar precios de venta correctos.',
  },
  {
    question: '¿Cómo se calcula el food cost de un restaurante?',
    answer: 'El food cost se calcula dividiendo el coste de los ingredientes de un plato entre su precio de venta y multiplicando por 100. Por ejemplo, si un plato tiene un coste de ingredientes de 4€ y se vende a 16€, su food cost es del 25%. El objetivo en hostelería es mantenerlo por debajo del 30-35%.',
  },
  {
    question: '¿Cuál es el porcentaje de food cost ideal para un restaurante?',
    answer: 'El food cost ideal varía según el tipo de negocio: en restaurantes a la carta suele situarse entre el 25% y el 35%; en bares y cafeterías puede ser inferior al 25%; en catering y colectividades, entre el 30% y el 40%. PLEKO usa un semáforo: verde (<30%), amarillo (30-35%) y rojo (>35%) para que lo controles de un vistazo.',
  },
  {
    question: '¿Qué son las mermas en cocina y cómo se calculan?',
    answer: 'Las mermas son las pérdidas de peso o volumen de un ingrediente durante su limpieza, cocción o procesado. Por ejemplo, un kilo de espinacas frescas puede perder el 30% al cocerse, dejando 700g útiles. PLEKO te permite registrar el porcentaje de merma de cada ingrediente y lo aplica automáticamente en todos tus escandallos.',
  },
  {
    question: '¿Puedo importar mis recetas desde Excel?',
    answer: 'Sí. PLEKO incluye importación masiva de ingredientes desde Excel con plantilla descargable. Para las recetas, puedes crearlas manualmente, generarlas con IA o usar el escáner OCR de albaranes para actualizar precios. La mayoría de clientes traslada su información en menos de una hora.',
  },
  {
    question: '¿Qué diferencia hay entre food cost teórico y food cost real?',
    answer: 'El food cost teórico es el que obtienes calculando los costes de tus recetas y escandallos. El food cost real incluye también mermas no controladas, robos, errores de producción y desperdicios. La diferencia entre ambos (desviación) te indica cuánto estás perdiendo en cocina. PLEKO te ayuda a identificar y reducir esa desviación.',
  },
  {
    question: '¿PLEKO funciona para catering y colectividades?',
    answer: 'Sí. PLEKO está diseñado para restaurantes, bares, cafeterías, catering y colectividades (hospitales, colegios, comedores de empresa). El cálculo de costes por ración, la escalabilidad de recetas y la gestión de proveedores se adaptan a cualquier volumen de producción.',
  },
];

const pricingHighlights = [
  'Sin tarjeta de crédito',
  '14 días de prueba gratis',
  'Cancela cuando quieras',
];

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-slate-50 transition-colors"
      >
        <span className="font-medium text-slate-900 pr-4">{question}</span>
        <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <div className={`px-6 pb-5 text-slate-600 text-sm leading-relaxed border-t border-slate-100 pt-4 ${open ? '' : 'hidden'}`}>
          {answer}
      </div>
    </div>
  );
}

export default function LandingPage() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  return (
    <div>
      <Helmet>
        <title>PLEKO — Software de Escandallos y Food Cost</title>
        <meta name="description" content="Software de escandallos para restaurantes: calcula costes de platos, controla el food cost en tiempo real y aumenta tu margen. Prueba gratis 14 días." />
        <meta name="keywords" content="software escandallos restaurante, food cost restaurante, control costes hostelería, fichas técnicas restaurante, programa escandallos cocina, gestión costes restaurante, mermas cocina, carta digital restaurante" />
        <link rel="canonical" href="https://pleko.app/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(item => ({
            "@type": "Question",
            "name": item.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": item.answer,
            }
          }))
        })}</script>
      </Helmet>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 sm:pt-16 lg:pt-20 pb-10 sm:pb-14">
          <div>
            <div className="text-center max-w-4xl mx-auto">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-5">
                <Star className="w-4 h-4" />
                Software especializado en escandallos para hostelería
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-5xl xl:text-6xl font-extrabold text-slate-900 mb-5 leading-[1.1] tracking-tight">
                <span className="text-blue-500">Escandallos</span> y{' '}
                <span className="text-blue-500">food cost</span>
                <br className="hidden sm:block" />{' '}
                de tu restaurante, bajo control
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 mb-6 max-w-2xl mx-auto leading-relaxed">
                Calcula costes automáticamente, controla mermas, escanea facturas con IA y detecta recetas no rentables. Deja el Excel.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-5">
                <Link
                  to="/register"
                  className="w-full sm:w-auto px-8 py-4 bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-600 transition-colors flex items-center justify-center gap-2 text-lg shadow-lg shadow-blue-200"
                >
                  Empezar gratis 14 días
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <button
                  onClick={() => setDemoModalOpen(true)}
                  className="w-full sm:w-auto px-8 py-4 border-2 border-blue-200 text-blue-600 font-semibold rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  Solicitar demo gratuita
                </button>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-1.5 text-sm text-slate-500">
                {pricingHighlights.map((highlight) => (
                  <div key={highlight} className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                    {highlight}
                  </div>
                ))}
              </div>

            </div>
          </div>
        </div>

      </section>

      {/* Chef trust section */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 items-center">
            <div className="relative mb-10 lg:mb-0">
              <div className="absolute -inset-4 bg-blue-100 rounded-3xl blur-3xl opacity-30" />
              <div className="relative rounded-2xl overflow-hidden shadow-xl max-w-md mx-auto lg:max-w-none">
                <img src="/chef-hero.jpg" alt="Chef profesional usando software de escandallos en cocina" className="w-full aspect-[4/3] object-cover object-top" loading="lazy" width={800} height={600} />
              </div>
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Software de escandallos hecho para hostelería
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                PLEKO no es un ERP genérico — es un software de escandallos diseñado exclusivamente para chefs y propietarios de restaurantes que necesitan controlar el food cost de verdad.
              </p>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">Escandallos en 2 minutos</p>
                    <p className="text-sm text-slate-500">Crea recetas, asigna ingredientes y obtén el food cost automáticamente.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">OCR con IA para facturas</p>
                    <p className="text-sm text-slate-500">Foto al albarán y los precios se actualizan solos en todas tus recetas.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-900">Mermas y despieces reales</p>
                    <p className="text-sm text-slate-500">Calcula el coste real contando las pérdidas. Nadie más lo hace así.</p>
                  </div>
                </div>
              </div>
              <div className="mt-8">
                <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors">
                  Prueba gratis 14 días <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Excel callout banner */}
      <section className="bg-amber-50 border-y border-amber-200 py-5">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-amber-800 font-medium">
            ¿Sigues haciendo escandallos en Excel? Cada vez que cambia un precio, tienes que actualizarlo a mano en decenas de hojas.{' '}
            <Link to="/register" className="underline font-semibold hover:text-amber-900">
              Con PLEKO se actualiza solo →
            </Link>
          </p>
        </div>
      </section>

      {/* Escandallo Video Demo */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">Cómo funciona el software de escandallos</h2>
            <p className="text-slate-500 max-w-lg mx-auto">De ingredientes a escandallo completo con food cost — en menos de 2 minutos.</p>
          </div>

          {/* 4-step process */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
            {[
              { step: '1', title: 'Añade ingredientes', desc: 'Con precios de proveedor o escaneando facturas con IA' },
              { step: '2', title: 'Crea tus recetas', desc: 'Ingredientes, cantidades, mermas y pasos de elaboración' },
              { step: '3', title: 'PLEKO calcula el escandallo', desc: 'Coste por ración, food cost y precio de venta sugerido' },
              { step: '4', title: 'Controla el food cost', desc: 'Semáforo de rentabilidad y alertas cuando algo cambia' },
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-10 h-10 bg-blue-500 text-white font-bold rounded-full flex items-center justify-center mx-auto mb-3 text-lg">{item.step}</div>
                <p className="font-semibold text-slate-900 text-sm mb-1">{item.title}</p>
                <p className="text-xs text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Autoplay video clip */}
          <div className="relative">
            <div className="absolute -inset-4 bg-blue-100 rounded-3xl blur-3xl opacity-30" />
            <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-black/10">
              <video
                src="/landing-demo.mp4"
                poster="/og-image.png"
                autoPlay
                muted
                loop
                playsInline
                className="w-full"
                width={1280}
                height={720}
              />
            </div>
          </div>

          {/* CTA: Ver demo completa */}
          <div className="text-center mt-8">
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white font-bold rounded-xl hover:bg-blue-600 transition-colors text-lg shadow-lg shadow-blue-200"
            >
              Empezar gratis 14 días
              <ArrowRight className="w-5 h-5" />
            </Link>
            <p className="mt-3 text-sm text-slate-400">Sin tarjeta de crédito. Configurado en 2 minutos.</p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              8 herramientas de gestión de costes en un solo software de escandallos
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Todo lo que necesitas para controlar el food cost de tu restaurante, sin contratar 3 herramientas distintas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="p-6 rounded-xl border border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-blue-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-slate-600">{feature.description}</p>
                </div>
              );
            })}
          </div>

          <div className="mt-12 text-center space-y-4">
            <Link to="/como-funciona" className="inline-flex items-center gap-2 text-blue-500 font-medium hover:text-blue-600">
              Ver todas las funcionalidades <ArrowRight className="w-4 h-4" />
            </Link>
            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
              <Link to="/escandallo" className="hover:text-blue-500 transition-colors">Qué es un escandallo</Link>
              <Link to="/food-cost" className="hover:text-blue-500 transition-colors">Calcular food cost</Link>
              <Link to="/mermas-cocina" className="hover:text-blue-500 transition-colors">Mermas en cocina</Link>
              <Link to="/calculadora-food-cost" className="hover:text-blue-500 transition-colors">Calculadora food cost</Link>
              <Link to="/plantilla-escandallo-excel" className="hover:text-blue-500 transition-colors">Plantilla escandallo Excel</Link>
              <Link to="/tabla-mermas-alimentos" className="hover:text-blue-500 transition-colors">Tabla de mermas</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Y además — features secundarias compactas */}
      <section className="py-16 bg-slate-900">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 text-center">
            Más que un programa de escandallos
          </h2>
          <p className="text-slate-400 text-center mb-10 max-w-2xl mx-auto">
            Carta digital y conexión con tu TPV — incluidos en tu plan, sin herramientas extra.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <Link to="/carta-digital-restaurante" className="bg-slate-800 rounded-xl p-6 hover:bg-slate-700 transition-colors block">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <QrCode className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Carta digital con QR</h3>
                  <span className="text-[10px] font-semibold bg-green-500/20 text-green-300 px-1.5 py-0.5 rounded">Incluido</span>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Diseña tu carta, genera un QR y compártela desde el móvil. Alérgenos automáticos. Sin apps, sin impresiones. Los precios se actualizan solos desde tus escandallos.
              </p>
            </Link>
            <div className="bg-slate-800 rounded-xl p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <BarChart3 className="w-5 h-5 text-blue-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-white">Integración con TPV</h3>
                  <span className="text-[10px] font-semibold bg-blue-500/20 text-blue-300 px-1.5 py-0.5 rounded">PRO</span>
                </div>
              </div>
              <p className="text-slate-400 text-sm leading-relaxed">
                Conecta Square o Last.app y cruza ventas reales con costes de recetas. Food cost real, labor cost y prime cost en tiempo real.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Stats */}
      <section className="py-12 sm:py-16 bg-blue-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8 text-center">
            {[
              { value: '<2 min', label: 'Por escandallo' },
              { value: '8 en 1', label: 'Herramientas integradas' },
              { value: '<30%', label: 'Objetivo food cost' },
              { value: '14 días', label: 'Prueba gratis' },
            ].map((stat) => (
              <div key={stat.label}>
                <div className="text-2xl sm:text-4xl font-bold text-white mb-1">{stat.value}</div>
                <div className="text-blue-100 text-xs sm:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 lg:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Lo que dicen los profesionales que ya controlan su food cost con PLEKO
            </h2>
            <p className="text-xl text-slate-600">
              Chefs y propietarios que dejaron el Excel y ahora saben exactamente lo que les cuesta cada plato.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial) => (
              <div
                key={testimonial.name}
                className="bg-white p-6 rounded-xl border border-slate-200"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 mb-6">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <div className="font-medium text-slate-900">{testimonial.name}</div>
                    <div className="text-sm text-slate-500">{testimonial.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Precios del software de escandallos
            </h2>
            <p className="text-xl text-slate-600">
              14 días de prueba gratis. Sin tarjeta de crédito.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 items-start">
            {[
              {
                name: 'BASIC',
                price: 49,
                original: 69,
                desc: 'Bares y restaurantes pequeños',
                color: 'green',
                features: ['Ingredientes y recetas ilimitados', 'Escandallos automáticos', 'Despieces con coste real', 'Mermas sugeridas por categoría', '15 escaneos de facturas/mes', 'Carta digital con QR', '1 usuario'],
              },
              {
                name: 'PRO',
                price: 79,
                original: 99,
                desc: 'Restaurantes independientes',
                color: 'blue',
                popular: true,
                features: ['Todo de BASIC', '60 escaneos de facturas/mes', 'Simulador de precios', 'Optimizador de recetas', 'Alertas de precios', 'Integración TPV (Square, Last.app)', '3 usuarios'],
              },
              {
                name: 'SCALE',
                price: 149,
                original: 179,
                desc: 'Grupos de restaurantes y catering',
                color: 'purple',
                features: ['Todo de PRO', '200 escaneos de facturas/mes', 'Precios de mercado mayorista', 'Laboratorio de recetas', 'Multi-restaurante', 'Hasta 10 usuarios'],
              },
            ].map((plan) => (
              <div
                key={plan.name}
                className={`relative rounded-2xl border p-6 ${
                  plan.popular
                    ? 'border-blue-400 shadow-lg shadow-blue-100 bg-blue-50'
                    : 'border-slate-200 bg-white'
                }`}
              >
                {plan.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-blue-500 text-white text-xs font-bold rounded-full whitespace-nowrap">
                    MÁS POPULAR
                  </span>
                )}
                <p className="font-bold text-slate-900 text-lg mb-1">{plan.name}</p>
                <p className="text-slate-500 text-sm mb-4">{plan.desc}</p>
                <div className="mb-5">
                  <span className="text-4xl font-bold text-slate-900">{plan.price}€</span>
                  <span className="text-slate-500 text-sm">/mes</span>
                  <span className="ml-2 text-sm text-slate-400 line-through">{plan.original}€</span>
                </div>
                <ul className="space-y-2 mb-6">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/register"
                  className={`block text-center py-2.5 rounded-xl font-semibold text-sm transition-colors ${
                    plan.popular
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'border border-slate-300 text-slate-700 hover:border-blue-300 hover:text-blue-600'
                  }`}
                >
                  Empezar gratis
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center text-slate-500 text-sm mt-8">
            Precios de lanzamiento para los primeros 100 clientes — garantizados de por vida.{' '}
            <Link to="/precios" className="text-blue-500 hover:underline">
              Ver comparativa completa de planes →
            </Link>
          </p>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 lg:py-28 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
              Preguntas frecuentes sobre escandallos y food cost
            </h2>
            <p className="text-xl text-slate-600">
              Todo lo que necesitas saber sobre la gestión de costes en restaurantes.
            </p>
          </div>
          <div className="space-y-3">
            {faqItems.map((item) => (
              <FaqItem key={item.question} question={item.question} answer={item.answer} />
            ))}
          </div>
          <p className="text-center text-slate-500 text-sm mt-8">
            ¿Tienes más preguntas?{' '}
            <a href="mailto:hello@pleko.app" className="text-blue-500 hover:underline">
              Escríbenos a hello@pleko.app
            </a>
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <ContactSection onRequestDemo={() => setDemoModalOpen(true)} />

      <DemoRequestModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />

      {/* CTA Section */}
      <section className="py-20 lg:py-32 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Empieza a controlar tu food cost hoy — gratis
          </h2>
          <p className="text-xl text-slate-400 mb-2">
            Prueba PLEKO gratis durante 14 días. Sin tarjeta de crédito.
          </p>
          <p className="text-slate-500 mb-8">
            Escandallos automáticos · Carta digital con QR · OCR de facturas con IA
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors"
          >
            Empezar prueba gratuita
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <WhatsAppButton />
    </div>
  );
}

function ContactSection({ onRequestDemo }: { onRequestDemo: () => void }) {
  return (
    <section id="contacto" className="py-20 lg:py-32 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
          ¿Necesitas ayuda con tus escandallos?
        </h2>
        <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
          Te hacemos una demo personalizada y te ayudamos a calcular el food cost de tus recetas.
          Sin compromiso.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-12">
          <div className="flex items-center gap-3 text-slate-600">
            <Clock className="w-5 h-5 text-blue-500" />
            <span>Respuesta en menos de 24h</span>
          </div>
          <div className="flex items-center gap-3 text-slate-600">
            <CheckCircle2 className="w-5 h-5 text-blue-500" />
            <span>Demo gratuita sin compromiso</span>
          </div>
        </div>

        <button
          onClick={onRequestDemo}
          className="inline-flex items-center gap-3 px-8 py-4 bg-blue-500 text-white font-semibold rounded-xl hover:bg-blue-600 transition-colors text-lg"
        >
          <Mail className="w-6 h-6" />
          Solicitar demo gratuita
        </button>
      </div>
    </section>
  );
}
